/**
 * Button theme overrides.
 *
 * fe-ui's Button sets `defaultProps: {size: null}`, which disables Chakra's size
 * scale, so it ships no height and no icon sizing at all. These base styles add
 * the missing pieces: 16px label, 40px height, 18x18 icon box.
 *
 * Don't use `defaultProps.size = 'md'` to get a height -- fe-ui shifts the font
 * scale, so Chakra's `md` size resolves `fontSize: 'md'` to 18px. `base` is 16px.
 */
export const baseStyles = {
    fontSize: 'base',
    // `height` only, no `minHeight`: fe-ui's `tertiary` variant unsets `h` but
    // not `minH`, so a `minHeight` here would trap inline text buttons at 40px.
    height: 10,
    // Overrides fe-ui's `py: 3`, which would push a tall icon out of the box.
    py: 0,
    lineHeight: '1.5rem',
    // 18px, not the spec's 24px: Figma's icons have padding inside their box
    // that `@spothero/icons` glyphs don't, so 24px renders larger than the mock.
    //
    // Sizing the wrapper's font works because Chakra's `Icon` is `1em` square,
    // and unlike `& svg {width: 6}` it won't outrank an icon's own `boxSize` --
    // explicit sizes (e.g. `Tooltip`'s `popoverStyleProps.icon`) still win.
    '& .chakra-button__icon': {
        fontSize: '1.125rem',
    },
} as const;

/**
 * Shared geometry for the small filter/search-control buttons, which the design
 * audit exempts from the button spec ("besides small filter button").
 *
 * A style-props object rather than a Chakra variant: only one variant applies at
 * a time, and fe-ui doesn't export its theme, so a `filter` variant would have
 * to duplicate `secondary`'s colours and drift from them.
 */
export const filterButtonStyleProps = {
    variant: 'secondary',
    // Positions the applied-filter-count Badge.
    position: 'relative',
    width: 20,
    color: 'black',
    // Exempt from the 16px/18px spec; keeps the size these had before.
    fontSize: 'sm',
    fontWeight: 'normal',
    paddingX: 3.5,
    paddingY: 3,
    sx: {svg: {width: 3.5, height: 3.5}},
} as const;

/**
 * Circular count badge for the filter buttons. `Badge` is a pill, so a single
 * digit renders wider than tall; an equal box with no horizontal padding makes
 * it a circle against the shared `Badge`'s `borderRadius: 'full'`.
 *
 * Use this for badges sitting inline in a `rightIcon` slot; use
 * `filterCountBadgeOverlayStyleProps` for ones pinned to the button's corner.
 */
export const filterCountBadgeStyleProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: 5,
    paddingX: 0,
    fontSize: 'xs',
    fontWeight: 'normal',
} as const;

/** Count badge overlaid on the button's top-right corner. */
export const filterCountBadgeOverlayStyleProps = {
    ...filterCountBadgeStyleProps,
    position: 'absolute',
    top: -1.5,
    right: -2,
} as const;
